import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  StyleSheet,
  ScrollView,
  RefreshControl,
  View,
  Platform,
  KeyboardAvoidingView,
} from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { router } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import StatusContainer from '@/components/StatusContainer';
import Button from '@/components/Buttons/Button';
import { useTranslation } from '@/hooks/useTranslation';
import MemoryEmptyState from '@/components/Placeholders/MemoryEmptyState';
import MemoryItem from '@/components/Settings/MemoryItem';
import MemoryListSkeleton from '@/components/Placeholders/MemoryListSkeleton';
import FloatingSelectButton from '@/components/Buttons/FloatingSelectButton';
import { useAuthStore, useToastStore, useMemoryStore, useModalStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import SignInCTAPanel from '@/components/Placeholders/SignInCTAPanel';
import { accountApi } from '@/lib/api';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BORDER_RADIUS } from '@/constants/config';
import moment from 'moment-mini';

import MemoryHero from '@/components/Memory/MemoryHero';
import MemoryOverviewCard from '@/components/Memory/MemoryOverviewCard';
import MemoryPreferencesCard from '@/components/Memory/MemoryPreferencesCard';
import MemoryQuickAdd from '@/components/Memory/MemoryQuickAdd';
import MemoryFilterTabs from '@/components/Memory/MemoryFilterTabs';
import { shouldUseVirtualizedMemoryList } from '@/constants/utils/memory_list';

const RECENTLY_LEARNED_MS = 7 * 24 * 60 * 60 * 1000;

const formatDate = (dateString) => {
  return moment(dateString).format('MMM D, YYYY');
};

const createStyles = (colors, isDark, isXS, isXL, insetBottom = 0) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.solid_color_primary,
      borderTopWidth: Platform.OS === 'web' ? 1 : 0,
      borderTopColor: colors.border_color_secondary,
    },
    contentWrapper: {
      width: '100%',
      maxWidth: isXL ? 1100 : 920,
      marginHorizontal: 'auto',
      flex: 1,
      flexDirection: 'column',
    },
    scrollContainer: {
      flex: 1,
      width: '100%',
    },
    scrollContainerContent: {
      width: '100%',
      paddingBottom: insetBottom + 160,
    },
    content: {
      padding: isXS ? 14 : 20,
      paddingTop: isXS ? 14 : 20,
      width: '100%',
    },
    sectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 10,
      paddingHorizontal: 4,
      marginTop: 10,
      marginBottom: 6,
    },
    sectionBadge: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 10,
      backgroundColor: colors.solid_color_tertiary,
    },
    memoryListCard: {
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      borderRadius: BORDER_RADIUS.large,
      overflow: 'hidden',
      backgroundColor: colors.solid_color_block,
    },
    memoryListItemShell: {
      borderBottomWidth: 1,
      borderBottomColor: colors.border_color_secondary,
    },
    // Empty search state
    emptySearchContainer: {
      paddingVertical: isXS ? 28 : 36,
      alignItems: 'flex-start',
      gap: 10,
      marginTop: 8,
    },
    emptySearchIconWrap: {
      width: 52,
      height: 52,
      borderRadius: 26,
      backgroundColor: colors.solid_color_tertiary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    emptySearchText: {
      alignItems: 'center',
      gap: 4,
    },
    emptySearchTitle: {
      textAlign: 'center',
    },
    emptySearchDescription: {
      textAlign: 'center',
      maxWidth: 380,
      lineHeight: 20,
    },
    emptySearchQuery: {
      fontStyle: 'italic',
      fontWeight: '600',
    },
    emptySearchTipsList: {
      gap: 6,
      marginTop: 4,
      alignSelf: 'center',
      width: '100%',
      maxWidth: 380,
    },
    emptySearchTipRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 8,
    },
    emptySearchTipBullet: {
      marginTop: 6,
      width: 4,
      height: 4,
      borderRadius: 2,
      backgroundColor: colors.text_color_tertiary,
    },
    emptySearchActionRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      justifyContent: 'center',
      marginTop: 4,
    },
  });

const MemoryPage = React.memo(function MemoryPage() {
  const { t } = useTranslation();
  const { colors, isDark } = useTheme();
  const { isXS, isXL } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const [refreshing, setRefreshing] = useState(false);

  const { addToast } = useToastStore((state) => state.actions);
  const { openModal } = useModalStore((state) => state.actions);
  const updateUserPreferences = useAuthStore((state) => state.updateUserPreferences);
  const userMemoryPreferences = useAuthStore((state) => state.user?.preferences?.memory);
  const isPlus = useAuthStore((state) => !!state.user?.flags?.isPlus);
  const userId = useAuthStore((state) => state.user?.id);
  const hasUser = Boolean(userId);

  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  const memoriesMap = useMemoryStore((state) => state.memories);
  const memoryIds = useMemoryStore((state) => state.memoryIds);
  const isLoading = useMemoryStore((state) => state.isLoading.fetch);
  const fetchError = useMemoryStore((state) => state.errors.fetch);

  const fetchMemories = useMemoryStore((state) => state.fetchMemories);
  const createMemory = useMemoryStore((state) => state.createMemory);
  const updateMemory = useMemoryStore((state) => state.updateMemory);
  const deleteMemory = useMemoryStore((state) => state.deleteMemory);
  const isCreating = useMemoryStore((state) => state.isLoading.create);

  const [isMemoryEnabled, setIsMemoryEnabled] = useState(userMemoryPreferences?.isEnabled !== false);
  const [isLearningEnabled, setIsLearningEnabled] = useState(
    userMemoryPreferences?.isAutoGenerationEnabled !== false &&
      userMemoryPreferences?.autoSaveEnabled !== false &&
      userMemoryPreferences?.dreamingEnabled !== false
  );

  // Quick Add state
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddDraft, setQuickAddDraft] = useState('');
  const [savingPreference, setSavingPreference] = useState(false);
  const preferenceRequest = useRef(false);
  const bulkDeleting = useRef(false);

  // Search / filter / sort / bulk-select state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'learned' | 'manual' | 'recent'
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' | 'oldest' | 'alphabetical'
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const scrollViewRef = useRef(null);
  const memoryListRef = useRef(null);

  const memories = useMemo(() => {
    return memoryIds.map((id) => memoriesMap[id]).filter(Boolean);
  }, [memoriesMap, memoryIds]);

  // Counts for filter pills
  const counts = useMemo(() => {
    let learned = 0;
    let manual = 0;
    let recent = 0;
    const cutoff = Date.now() - RECENTLY_LEARNED_MS;

    for (const m of memories) {
      const source = m.source || (m.isAutoGenerated ? 'auto' : 'manual');
      if (source === 'manual' || source === 'import') {
        manual++;
      } else {
        learned++;
      }

      const activityAt = Math.max(
        new Date(m.lastConfirmedAt || 0).getTime(),
        new Date(m.updatedAt || 0).getTime(),
        new Date(m.createdAt || 0).getTime()
      );
      if (activityAt >= cutoff) {
        recent++;
      }
    }

    return {
      all: memories.length,
      learned,
      manual,
      recent,
    };
  }, [memories]);

  // Filtered and sorted memories
  const filteredMemories = useMemo(() => {
    let result = memories;

    // Filter by category tab
    if (activeFilter === 'learned') {
      result = result.filter((m) => {
        const source = m.source || (m.isAutoGenerated ? 'auto' : 'manual');
        return source === 'auto' || source === 'dream' || source === 'explicit';
      });
    } else if (activeFilter === 'manual') {
      result = result.filter((m) => {
        const source = m.source || (m.isAutoGenerated ? 'auto' : 'manual');
        return source === 'manual' || source === 'import';
      });
    } else if (activeFilter === 'recent') {
      const cutoff = Date.now() - RECENTLY_LEARNED_MS;
      result = result.filter((m) => {
        const activityAt = Math.max(
          new Date(m.lastConfirmedAt || 0).getTime(),
          new Date(m.updatedAt || 0).getTime(),
          new Date(m.createdAt || 0).getTime()
        );
        return activityAt >= cutoff;
      });
    }

    // Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((m) => m.content?.toLowerCase().includes(q));
    }

    // Sorting
    const sorted = [...result];
    if (sortOrder === 'oldest') {
      sorted.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    } else if (sortOrder === 'alphabetical') {
      sorted.sort((a, b) => (a.content || '').localeCompare(b.content || ''));
    } else {
      // Newest first
      sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }

    return sorted;
  }, [memories, activeFilter, searchQuery, sortOrder]);

  // Split into manual vs learned for sectioned view when activeFilter === 'all' and no search
  const { manualMemories, learnedMemories } = useMemo(() => {
    if (activeFilter !== 'all' || searchQuery.trim() || sortOrder !== 'newest') {
      return { manualMemories: [], learnedMemories: [] };
    }
    const manual = [];
    const learned = [];
    for (const m of filteredMemories) {
      const source = m.source || (m.isAutoGenerated ? 'auto' : 'manual');
      if (source === 'manual' || source === 'import') {
        manual.push(m);
      } else {
        learned.push(m);
      }
    }
    return { manualMemories: manual, learnedMemories: learned };
  }, [filteredMemories, activeFilter, searchQuery, sortOrder]);

  const shouldShowSections =
    activeFilter === 'all' &&
    !searchQuery.trim() &&
    sortOrder === 'newest' &&
    manualMemories.length > 0 &&
    learnedMemories.length > 0;

  // FlashList data with interleaved section headers for native
  const sectionedMemories = useMemo(() => {
    if (!shouldShowSections) return filteredMemories;
    return [
      {
        id: 'section-learned',
        type: 'section-header',
        label: t('pages.memory.learnedSection'),
        count: learnedMemories.length,
      },
      ...learnedMemories,
      {
        id: 'section-manual',
        type: 'section-header',
        label: t('pages.memory.manualSection'),
        count: manualMemories.length,
      },
      ...manualMemories,
    ];
  }, [shouldShowSections, filteredMemories, learnedMemories, manualMemories, t]);

  useEffect(() => {
    const visible = new Set(filteredMemories.map(memory => memory.id));
    setSelectedIds(previous => {
      const next = new Set([...previous].filter(id => visible.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [filteredMemories]);

  const selectedCount = selectedIds.size;

  const styles = useMemo(
    () => createStyles(colors, isDark, isXS, isXL, insets.bottom),
    [colors, isDark, isXS, isXL, insets.bottom]
  );

  useEffect(() => {
    if (!hasUser) return;
    fetchMemories().catch((err) => {
      console.error('Error fetching memories:', err);
      addToast(t('pages.memory.loadFailed'), 'error');
    });
  }, [fetchMemories, addToast, hasUser, t]);

  useEffect(() => {
    setIsMemoryEnabled(userMemoryPreferences?.isEnabled !== false);
    setIsLearningEnabled(
      userMemoryPreferences?.isAutoGenerationEnabled !== false &&
        userMemoryPreferences?.autoSaveEnabled !== false &&
        userMemoryPreferences?.dreamingEnabled !== false
    );
  }, [
    userMemoryPreferences?.isEnabled,
    userMemoryPreferences?.isAutoGenerationEnabled,
    userMemoryPreferences?.autoSaveEnabled,
    userMemoryPreferences?.dreamingEnabled,
  ]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchMemories();
    } catch (_err) {
      addToast(t('pages.memory.loadFailed'), 'error');
    } finally {
      setRefreshing(false);
    }
  }, [fetchMemories, addToast, t]);

  const handleUpdateMemory = useCallback(
    async (memoryId, content) => {
      try {
        await updateMemory(memoryId, content);
        addToast(t('pages.memory.updated'), 'success');
        return true;
      } catch (err) {
        console.error('Error updating memory:', err);
        addToast(t('pages.memory.updateFailed'), 'error');
        return false;
      }
    },
    [updateMemory, addToast, t]
  );

  const handleDeleteMemory = useCallback(
    async (memoryId) => {
      try {
        await deleteMemory(memoryId);
        addToast(t('pages.memory.deleted'), 'success');
      } catch (err) {
        console.error('Error deleting memory:', err);
        addToast(t('pages.memory.deleteFailed'), 'error');
      }
    },
    [deleteMemory, addToast, t]
  );

  const handleToggleSelect = useCallback((memoryId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(memoryId)) {
        next.delete(memoryId);
      } else {
        next.add(memoryId);
      }
      return next;
    });
  }, []);

  const handleToggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => {
      if (prev) {
        setSelectedIds(new Set());
        return false;
      }
      return true;
    });
  }, []);

  const handleExitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(filteredMemories.map((m) => m.id)));
  }, [filteredMemories]);

  const handleBulkDelete = useCallback(() => {
    const count = selectedIds.size;
    if (count === 0 || bulkDeleting.current) return;

    showConfirmationModal({
      title: t('pages.memory.bulkDeleteTitle').replace('%COUNT%', count),
      message: t('pages.memory.bulkDeleteMessage').replace('%COUNT%', count),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
      isDangerous: true,
      onConfirm: async () => {
        if (bulkDeleting.current) return;
        bulkDeleting.current = true;
        let successCount = 0;
        const failedIds = [];
        const ids = Array.from(selectedIds);
        for (const id of ids) {
          try {
            await deleteMemory(id);
            successCount++;
          } catch (err) {
            failedIds.push(id);
            console.error('Error bulk deleting memory:', err);
          }
        }
        setSelectedIds(new Set(failedIds));
        setSelectionMode(failedIds.length > 0);
        bulkDeleting.current = false;

        if (successCount === ids.length) {
          addToast(t('pages.memory.bulkDeleted').replace('%COUNT%', successCount), 'success');
        } else if (successCount > 0) {
          addToast(t('pages.memory.bulkDeletePartial', { saved: successCount, failed: failedIds.length }), 'warning');
        } else {
          addToast(t('pages.memory.bulkDeleteFailed'), 'error');
        }
      },
    });
  }, [selectedIds, deleteMemory, addToast, showConfirmationModal, t]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleMemoryEnabledToggle = useCallback(
    async (value) => {
      if (preferenceRequest.current) return;
      preferenceRequest.current = true;
      setSavingPreference(true);
      try {
        setIsMemoryEnabled(value);
        await accountApi.updateMemoryPreferences(value, undefined);
        updateUserPreferences({ memory: { isEnabled: value } });
        addToast(
          value ? t('pages.memory.memoryEnabled') : t('pages.memory.memoryDisabled'),
          'success'
        );
      } catch (error) {
        console.error('Error updating memory preference:', error);
        setIsMemoryEnabled(!value);
        addToast(t('pages.memory.memoryPrefFailed'), 'error');
      } finally {
        preferenceRequest.current = false;
        setSavingPreference(false);
      }
    },
    [updateUserPreferences, addToast, t]
  );

  const handleLearningToggle = useCallback(
    async (value) => {
      if (preferenceRequest.current) return;
      preferenceRequest.current = true;
      setSavingPreference(true);
      try {
        setIsLearningEnabled(value);
        await accountApi.updateMemoryPreferences(undefined, value, value, value);
        updateUserPreferences({
          memory: {
            isAutoGenerationEnabled: value,
            autoSaveEnabled: value,
            dreamingEnabled: value,
          },
        });
        addToast(
          value ? t('pages.memory.learnEnabled') : t('pages.memory.learnDisabled'),
          'success'
        );
      } catch (error) {
        console.error('Error updating learning preference:', error);
        setIsLearningEnabled(!value);
        addToast(t('pages.memory.prefUpdateFailed'), 'error');
      } finally {
        preferenceRequest.current = false;
        setSavingPreference(false);
      }
    },
    [updateUserPreferences, addToast, t]
  );

  const handleSaveProfileSummary = useCallback(
    async (nextSummary) => {
      try {
        const result = await accountApi.updateMemoryProfile(nextSummary);
        const saved = result?.profileSummary ?? nextSummary;
        updateUserPreferences({
          memory: {
            profileSummary: saved,
            profileSummaryUserEdited: result?.profileSummaryUserEdited !== false,
          },
        });
        addToast(t('pages.memory.profileSummarySaved'), 'success');
        return true;
      } catch (error) {
        console.error('Error updating memory overview:', error);
        addToast(t('pages.memory.profileSummarySaveFailed'), 'error');
        return false;
      }
    },
    [updateUserPreferences, addToast, t]
  );

  const handleQuickAddMemory = useCallback(
    async (content) => {
      try {
        await createMemory(content);
        setShowQuickAdd(false);
        setQuickAddDraft('');
        setSearchQuery('');
        setActiveFilter('all');
        setSortOrder('newest');
        addToast(t('pages.memory.created'), 'success');
        return true;
      } catch (error) {
        console.error('Error creating memory:', error);
        addToast(t('pages.memory.createFailed'), 'error');
        return false;
      }
    },
    [createMemory, addToast, t]
  );

  const handleOpenMemoryBuilder = useCallback(() => {
    openModal('MemoryBuilderModal');
  }, [openModal]);

  const handleOpenImportMemory = useCallback(() => {
    openModal('ImportMemoryModal');
  }, [openModal]);

  const handlePlusUpsell = useCallback(() => {
    if (Platform.OS === 'ios' || Platform.OS === 'android' || Platform.OS === 'macos') {
      router.push('/(auth)/paywall');
      return;
    }
    openModal('PricingModal', {
      title: t('pages.memory.learnPlusUpsell'),
      subtitle: t('pages.memory.learnFromConversationsDescFree'),
    });
  }, [openModal, t]);

  // FlashList 1.x can trip React Native 0.79 Fabric's Yoga-owner assertion while
  // recycling this screen's variable-height rows and large editable header on
  // both iOS and Android. Use the bounded ScrollView path on Fabric; legacy
  // Android can retain virtualization for unusually large memory collections.
  const useNativeVirtualizedList = shouldUseVirtualizedMemoryList({
    platform: Platform.OS,
    fabricEnabled:
      typeof globalThis !== 'undefined' && globalThis.nativeFabricUIManager != null,
    memoryCount: memories.length,
  });

  const memoryRefreshControl = useMemo(
    () => (
      <RefreshControl
        refreshing={refreshing}
        onRefresh={onRefresh}
        tintColor={colors.text_color_secondary}
        colors={[colors.text_color_secondary]}
      />
    ),
    [refreshing, onRefresh, colors.text_color_secondary]
  );

  const renderSectionHeader = useCallback(
    (label, count, key) => (
      <View style={styles.sectionHeader} key={key || label}>
        <Text size="s" weight="bold" color="primary">
          {label}
        </Text>
        <View style={styles.sectionBadge}>
          <Text size="xs" color="secondary" weight="semibold">
            {count}
          </Text>
        </View>
      </View>
    ),
    [styles.sectionHeader, styles.sectionBadge]
  );

  const renderMemoryList = (items) => (
    <View style={styles.memoryListCard}>
      {items.map((memory) => (
        <MemoryItem
          key={memory.id}
          memory={memory}
          onUpdate={handleUpdateMemory}
          onDelete={handleDeleteMemory}
          formatDate={formatDate}
          showCheckbox={selectionMode}
          isSelected={selectedIds.has(memory.id)}
          onSelect={handleToggleSelect}
        />
      ))}
    </View>
  );

  const renderMemoryItem = useCallback(
    ({ item }) => {
      if (item.type === 'section-header') {
        return renderSectionHeader(item.label, item.count);
      }
      const memory = item;
      return (
        <View style={styles.memoryListItemShell}>
          <MemoryItem
            memory={memory}
            onUpdate={handleUpdateMemory}
            onDelete={handleDeleteMemory}
            formatDate={formatDate}
            showCheckbox={selectionMode}
            isSelected={selectedIds.has(memory.id)}
            onSelect={handleToggleSelect}
          />
        </View>
      );
    },
    [
      handleUpdateMemory,
      handleDeleteMemory,
      selectionMode,
      selectedIds,
      handleToggleSelect,
      renderSectionHeader,
      styles.memoryListItemShell,
    ]
  );

  const memoryKeyExtractor = useCallback((memory) => memory.id, []);

  const renderMainContent = () => {
    if (isLoading && !memories.length) {
      return <MemoryListSkeleton />;
    }

    if (fetchError && !memories.length) {
      return (
        <StatusContainer
          type="error"
          message={fetchError}
          actionText={t('pages.common.retry')}
          onAction={onRefresh}
        />
      );
    }

    return (
      <>
        {/* Masthead Hero */}
        <MemoryHero
          memories={memories}
          isCreating={isCreating}
          onOpenAdd={() => setShowQuickAdd((prev) => !prev)}
          isAdding={showQuickAdd}
          onOpenBuilder={handleOpenMemoryBuilder}
          onOpenImport={handleOpenImportMemory}
        />

        {/* Inline Quick Add Composer */}
        {showQuickAdd && (
          <MemoryQuickAdd
            text={quickAddDraft}
            onChangeText={setQuickAddDraft}
            onAddMemory={handleQuickAddMemory}
            onCancel={() => setShowQuickAdd(false)}
            isCreating={isCreating}
          />
        )}

        {/* Living Profile Overview Card */}
        <MemoryOverviewCard
          profileSummary={userMemoryPreferences?.profileSummary}
          isPending={isPlus && isLearningEnabled && isMemoryEnabled && !userMemoryPreferences?.profileSummary}
          isUserEdited={userMemoryPreferences?.profileSummaryUserEdited === true}
          canEdit={hasUser}
          onSave={handleSaveProfileSummary}
        />

        {/* Memory Preferences Card */}
        <MemoryPreferencesCard
          isMemoryEnabled={isMemoryEnabled}
          isSaving={savingPreference}
          onToggleMemoryEnabled={handleMemoryEnabledToggle}
          isLearningEnabled={isLearningEnabled}
          onToggleLearning={handleLearningToggle}
          isPlus={isPlus}
          onPlusUpsell={handlePlusUpsell}
        />

        {/* If no memories stored yet, display overhauled Empty State */}
        {!memories.length ? (
          <MemoryEmptyState onSelectExample={(example) => {
            setQuickAddDraft(example);
            setShowQuickAdd(true);
            scrollViewRef.current?.scrollTo({ y: 0, animated: true });
            memoryListRef.current?.scrollToOffset({ offset: 0, animated: true });
          }} />
        ) : (
          <>
            {/* Filter Tabs & Search Bar */}
            <MemoryFilterTabs
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              counts={counts}
              sortOrder={sortOrder}
              onSortChange={setSortOrder}
              resultCount={filteredMemories.length}
              selectionMode={selectionMode}
              onToggleSelectionMode={handleToggleSelectionMode}
            />

            {/* Empty search results */}
            {filteredMemories.length === 0 ? (
              <View style={styles.emptySearchContainer}>
                <View style={styles.emptySearchIconWrap}>
                  <Icon name="magnifying-glass" size={22} color={colors.text_color_secondary} />
                </View>
                <View style={styles.emptySearchText}>
                  <Text size="m" weight="bold" color="primary" style={styles.emptySearchTitle}>
                    {t('pages.memory.emptySearchTitle')}
                  </Text>
                  <Text size="s" color="secondary" style={styles.emptySearchDescription}>
                    {t('pages.memory.emptySearchDescription')}{' '}
                    {searchQuery.trim() ? (
                      <Text size="s" color="primary" style={styles.emptySearchQuery}>
                        "{searchQuery}"
                      </Text>
                    ) : null}
                  </Text>
                </View>
                <View style={styles.emptySearchTipsList}>
                  {[t('pages.memory.emptySearchTip1'), t('pages.memory.emptySearchTip2')].map(
                    (tip, i) => (
                      <View key={i} style={styles.emptySearchTipRow}>
                        <View style={styles.emptySearchTipBullet} />
                        <Text size="xs" color="secondary" style={{ flex: 1 }}>
                          {tip}
                        </Text>
                      </View>
                    )
                  )}
                </View>
                <View style={styles.emptySearchActionRow}>
                  <Button
                    text={t('pages.memory.clearSearchAndFilters')}
                    variant="transparent"
                    size="medium"
                    onPress={() => {
                      handleClearSearch();
                      setActiveFilter('all');
                    }}
                  />
                </View>
              </View>
            ) : useNativeVirtualizedList ? null : (
              (() => {
                if (!shouldShowSections) {
                  return renderMemoryList(filteredMemories);
                }
                return (
                  <>
                    {renderSectionHeader(
                      t('pages.memory.learnedSection'),
                      learnedMemories.length,
                      'sec-learned'
                    )}
                    {renderMemoryList(learnedMemories)}
                    {renderSectionHeader(
                      t('pages.memory.manualSection'),
                      manualMemories.length,
                      'sec-manual'
                    )}
                    {renderMemoryList(manualMemories)}
                  </>
                );
              })()
            )}
          </>
        )}
      </>
    );
  };

  if (!hasUser) {
    return (
      <View style={styles.container}>
        <MetaHead
          title={t('pages.memory.title')}
          description={t('pages.memory.description')}
          noindex
        />
        <View style={styles.contentWrapper}>
          <SignInCTAPanel
            icon="brain"
            source="memory_index"
            title="Sign in to use Memory"
            subtitle="Teach Gab who you are once and have it remember your preferences across every conversation."
            benefits={[
              { icon: 'brain', label: 'Persist preferences (tone, languages, defaults) across chats.' },
              { icon: 'sliders', label: 'Edit, delete, or pause individual memories anytime.' },
              { icon: 'wand-magic-sparkles', label: 'Gab learns durable facts as you chat — edit or delete anytime.' },
            ]}
          />
        </View>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
    >
      <MetaHead
        title={t('pages.memory.title')}
        description={t('pages.memory.description')}
        noindex
      />
      <View style={styles.contentWrapper}>
        {useNativeVirtualizedList ? (
          <View style={[styles.scrollContainer, filteredMemories.length > 0 && styles.memoryListCard]}>
            <FlashList
              ref={memoryListRef}
              data={sectionedMemories}
              renderItem={renderMemoryItem}
              keyExtractor={memoryKeyExtractor}
              getItemType={(item) => (item.type === 'section-header' ? 'section-header' : 'memory')}
              estimatedItemSize={88}
              extraData={selectedIds}
              ListHeaderComponent={
                <View style={styles.content}>
                  {renderMainContent()}
                </View>
              }
              style={{ flex: 1 }}
              contentContainerStyle={styles.scrollContainerContent}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              refreshControl={memoryRefreshControl}
            />
          </View>
        ) : (
          <ScrollView
            ref={scrollViewRef}
            style={styles.scrollContainer}
            contentContainerStyle={styles.scrollContainerContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            refreshControl={memoryRefreshControl}
          >
            <View style={styles.content}>
              {renderMainContent()}
            </View>
          </ScrollView>
        )}
      </View>

      {/* Floating bulk actions bar */}
      {selectionMode && selectedCount > 0 && (
        <FloatingSelectButton
          title={
            selectedCount === 1
              ? t('pages.memory.memorySelected').replace('%COUNT%', selectedCount)
              : t('pages.memory.memoriesSelected').replace('%COUNT%', selectedCount)
          }
          onClear={handleExitSelectionMode}
          actions={[
            ...(selectedCount < filteredMemories.length
              ? [
                  {
                    text: t('pages.memory.selectAll'),
                    icon: 'check-double',
                    onPress: handleSelectAll,
                    variant: 'primary',
                  },
                ]
              : []),
            {
              text: t('pages.memory.bulkDelete'),
              icon: 'trash',
              onPress: handleBulkDelete,
              variant: 'danger',
            },
          ]}
        />
      )}
    </KeyboardAvoidingView>
  );
});

function MemoryRoute() {
  return <MemoryPage />;
}

export default React.memo(MemoryRoute);
